import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaEnvelope,
  FaArrowLeft,
  FaShieldAlt,
  FaRocket,
  FaStar,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:5001";
  }

  return "https://vtugenx.in";
};

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hide footer
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      window.location.replace(`${apiBaseUrl}/api/auth/verify-email/${token}`);
    } catch (error) {
      setStatus("error");
      setMessage(
        error.response?.data?.message ||
          "Verification failed. Please try again.",
      );
    }
  };

  const resendVerification = async () => {
    try {
      toast.error("Please contact support or register again.");
    } catch (error) {
      toast.error("Failed to resend verification");
    }
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.9)" : "rgba(255, 255, 255, 0.95)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const iconVariants = {
    hidden: { scale: 0 },
    visible: {
      scale: 1,
      transition: { type: "spring", stiffness: 400, damping: 20 },
    },
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          maxWidth: "500px",
          width: "100%",
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          padding: isMobile ? "2rem" : "2.5rem",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          border: `1px solid ${getBorderColor()}`,
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            borderRadius: "32px 32px 0 0",
          }}
        />

        {/* Back Button */}
        <motion.div
          variants={itemVariants}
          style={{ position: "absolute", top: "1.5rem", left: "1.5rem" }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              border: "none",
              padding: "0.5rem",
              borderRadius: "10px",
              cursor: "pointer",
              color: getTextSecondary(),
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
              fontSize: "0.8rem",
              transition: "all 0.3s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = isDarkMode
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.1)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)")
            }
          >
            <FaArrowLeft size={12} /> Back
          </button>
        </motion.div>

        <AnimatePresence mode="wait">
          {status === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                variants={iconVariants}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    width: "80px",
                    height: "80px",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
                  }}
                >
                  <FaEnvelope size={40} color="white" />
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  style={{ display: "inline-block" }}
                >
                  <FaSpinner size={40} color="#8b5cf6" />
                </motion.div>
              </motion.div>

              <motion.h2
                variants={itemVariants}
                style={{
                  marginTop: "1.5rem",
                  marginBottom: "0.5rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontWeight: 700,
                }}
              >
                Verifying Email...
              </motion.h2>
              <motion.p
                variants={itemVariants}
                style={{ color: getTextSecondary() }}
              >
                Please wait while we verify your email address.
              </motion.p>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <motion.div
                variants={iconVariants}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    boxShadow: "0 20px 40px -12px rgba(16,185,129,0.4)",
                  }}
                >
                  <FaCheckCircle size={45} color="white" />
                </div>
              </motion.div>

              <motion.h2
                variants={itemVariants}
                style={{
                  marginBottom: "0.5rem",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontWeight: 700,
                }}
              >
                Email Verified!
              </motion.h2>
              <motion.p
                variants={itemVariants}
                style={{ color: getTextSecondary(), marginBottom: "1rem" }}
              >
                {message}
              </motion.p>
              <motion.p
                variants={itemVariants}
                style={{
                  color: getTextSecondary(),
                  fontSize: "0.85rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                <FaRocket size={14} color="#8b5cf6" />
                Redirecting to dashboard...
              </motion.p>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <motion.div
                variants={iconVariants}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                    boxShadow: "0 20px 40px -12px rgba(239,68,68,0.4)",
                  }}
                >
                  <FaExclamationTriangle size={45} color="white" />
                </div>
              </motion.div>

              <motion.h2
                variants={itemVariants}
                style={{
                  marginBottom: "0.5rem",
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontWeight: 700,
                }}
              >
                Verification Failed
              </motion.h2>
              <motion.p
                variants={itemVariants}
                style={{ color: getTextSecondary(), marginBottom: "1.5rem" }}
              >
                {message}
              </motion.p>

              <motion.div
                variants={itemVariants}
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/register"
                    style={{
                      padding: "0.8rem 1.5rem",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "12px",
                      fontWeight: "600",
                      display: "inline-block",
                      boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                    }}
                  >
                    Register Again
                  </Link>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/login"
                    style={{
                      padding: "0.8rem 1.5rem",
                      background: isDarkMode
                        ? "rgba(100,116,139,0.3)"
                        : "#e2e8f0",
                      color: getTextColor(),
                      textDecoration: "none",
                      borderRadius: "12px",
                      fontWeight: "500",
                      display: "inline-block",
                    }}
                  >
                    Go to Login
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Security Badge */}
        <motion.div
          variants={itemVariants}
          style={{
            marginTop: "2rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${getBorderColor()}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            Secure email verification with 256-bit encryption
          </span>
          <FaStar size={10} color="#f59e0b" />
        </motion.div>
      </motion.div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default VerifyEmail;
